import { Component, type ErrorInfo, type ReactNode } from 'react';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly crashed: boolean;
}

/**
 * Last line of defence for a render-time crash.
 *
 * The app had no boundary of any kind, so any exception thrown while rendering
 * unmounted the whole tree and left a blank white page — indistinguishable, to
 * the person looking at it, from the site being down (error audit, 2026-09-11).
 *
 * It deliberately shows nothing about the error. The stack is a developer
 * artefact and belongs in the console and the browser's own error reporting,
 * not on an investor's screen. What the user gets instead is the one action
 * that usually works, and a way back to somewhere known-good.
 */
export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only: this is the one place a stack trace is useful and safe.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})`,
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: '100%',
            borderRadius: 16,
            padding: 32,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <h1 style={{ margin: '0 0 12px', fontSize: 20, color: '#FFFFFF' }}>
            This page did not load correctly
          </h1>
          <p
            style={{
              margin: '0 0 24px',
              fontSize: 14,
              lineHeight: 1.6,
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            Nothing you were working on has been lost. Reloading usually fixes
            it. If it keeps happening, please contact support.
          </p>
          <button
            type="button"
            onClick={() => globalThis.location.reload()}
            style={{
              border: 'none',
              borderRadius: 12,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              color: NAVY,
              background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
            }}
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
